import { transformSDKBytesToRESTBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformRESTBytesToSDKBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixIdentityOauth2V1Oauth2NgUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/oauth2',
        destPath: '/v1/oauth',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/oauth2',
        destPath: '/v1/oauth',
      },
      {
        srcPath: '/oauth2/token_info',
        destPath: '/v1/token_info',
      },
    ],
    'users._base_domain_': [
      {
        srcPath: '/v1/oauth/device/verify',
        destPath: '/v1/oauth/device/verify',
      },
      {
        srcPath: '/v1/oauth/manage/user-code',
        destPath: '/v1/oauth/manage/user-code',
      },
      {
        srcPath: '/v2/oauth/device/verify',
        destPath: '/v2/oauth/device/verify',
      },
      {
        srcPath: '/v1/oauth/authorize',
        destPath: '/v1/oauth/authorize',
      },
      {
        srcPath: '/v1/oauth/user-info',
        destPath: '/v1/oauth/user-info',
      },
      {
        srcPath: '/iam/wix-idp/v1/oauth/token',
        destPath: '/v1/oauth/token',
      },
      {
        srcPath: '/iam/wix-idp/v1/oauth/user-info',
        destPath: '/v1/oauth/user-info',
      },
    ],
    _: [
      {
        srcPath: '/_api/oauth2',
        destPath: '/v1/oauth',
      },
    ],
    'platform.rise.ai': [
      {
        srcPath: '/oauth2',
        destPath: '/v1/oauth',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/oauth2-ng',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/oauth2/callback',
        destPath: '/oauth2/callback',
      },
      {
        srcPath: '/oauth2',
        destPath: '/v1/oauth',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/oauth2/callback',
        destPath: '/oauth2/callback',
      },
      {
        srcPath: '/oauth2/user-authorized',
        destPath: '/oauth2/user-authorized',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/oauth2/callback',
        destPath: '/oauth2/callback',
      },
      {
        srcPath: '/oauth2/user-authorized',
        destPath: '/oauth2/user-authorized',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/oauth2/callback',
        destPath: '/oauth2/callback',
      },
      {
        srcPath: '/oauth2/user-authorized',
        destPath: '/oauth2/user-authorized',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_identity_oauth';

/**
 * Creates an access token.
 *
 *
 * The endpoint accepts raw HTTP requests. You must pass the request's body
 * parameters formatted as bytes in the raw HTTP request's `body` field,
 * following this template:
 * `{"grantType": "client_credentials", "client_id": "<APP_ID>", "client_secret": "<APP_SECRET_KEY>", "instance_id": "<INSTANCE_ID>"}`.
 *
 * When the call succeeds, Wix returns `{"statusCode": 200}` and the created access
 * token in the `body` field of the raw HTTP response.
 *
 * In case the call fails, Wix returns the relevant `4XX` error code in the raw
 * HTTP response's `statusCode` field and details
 * about the error in `body`. Error details follow the
 * [conventions of the Internet Engineering Task Force (IETF)](https://datatracker.ietf.org/doc/html/rfc6749#appendix-A.7).
 */
export function token(payload: object): RequestOptionsFactory<any> {
  function __token({ host }: any) {
    const serializedData = transformPaths(payload, [
      { transformFn: transformSDKBytesToRESTBytes, paths: [{ path: 'body' }] },
    ]);
    const metadata = {
      entityFqdn: 'wix.identity.oauth.v1.refresh_token',
      method: 'POST' as any,
      methodFqn: 'wix.identity.oauth2.v1.Oauth2Ng.Token',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIdentityOauth2V1Oauth2NgUrl({
        protoPath: '/v1/oauth/token',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: [{ path: 'body' }],
          },
        ]),
    };

    return metadata;
  }

  return __token;
}

/** Token Introspection Endpoint. */
export function tokenInfo(payload: object): RequestOptionsFactory<any> {
  function __tokenInfo({ host }: any) {
    const serializedData = transformPaths(payload, [
      { transformFn: transformSDKBytesToRESTBytes, paths: [{ path: 'body' }] },
    ]);
    const metadata = {
      entityFqdn: 'wix.identity.oauth.v1.refresh_token',
      method: 'POST' as any,
      methodFqn: 'wix.identity.oauth2.v1.Oauth2Ng.TokenInfo',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIdentityOauth2V1Oauth2NgUrl({
        protoPath: '/v1/oauth/token-info',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __tokenInfo;
}
